import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { edgeColorClass, formatEdge } from '@/lib/format-edge';
import { formatOdds } from '@/lib/format-odds';
import { cn } from '@/lib/utils';

// Sub-components for ExecuteDialog. Split out so the dialog file stays
// under 300 LOC.

interface BetSummaryShape {
  market?: string;
  selection?: string;
  match?: { p1Name: string | null; p2Name: string | null } | null;
  currentBestBook: string | null;
  currentBestOdds: number | null;
  currentEdgePct?: number | null;
}

// Read-only context block at the top of the modal. The operator already
// saw this on the card; repeating it here makes the modal self-sufficient
// and prevents misclick mistakes (wrong match).
export function BetSummary({ opportunity }: { opportunity: BetSummaryShape }) {
  const edge = opportunity.currentEdgePct;
  const matchTitle =
    opportunity.match?.p1Name && opportunity.match?.p2Name
      ? `${opportunity.match.p1Name} vs ${opportunity.match.p2Name}`
      : null;
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
      {matchTitle && <div className="font-semibold text-foreground">{matchTitle}</div>}
      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono text-xs">
        {opportunity.market && (
          <span className="text-muted-foreground">{opportunity.market.toUpperCase()}</span>
        )}
        {opportunity.selection && (
          <span className="font-medium text-foreground">{opportunity.selection}</span>
        )}
        <span>
          @{' '}
          <span className="font-semibold text-foreground">
            {formatOdds(opportunity.currentBestOdds)}
          </span>{' '}
          <span className="text-muted-foreground">{opportunity.currentBestBook ?? '—'}</span>
        </span>
        {edge !== null && edge !== undefined && (
          <span className={cn('font-semibold', edgeColorClass(edge))}>{formatEdge(edge)}</span>
        )}
      </div>
    </div>
  );
}

// Quick-picker chips next to the Stake label. Clicking fills the input,
// saving a copy/paste from helper text.
export function StakeQuickChips({
  manual,
  kelly,
  onPick,
}: {
  manual: number;
  kelly: number;
  onPick: (n: number) => void;
}) {
  const hasManual = manual > 0;
  const hasKelly = kelly > 0;
  if (!hasManual && !hasKelly) {
    return <span className="text-[10px] font-mono text-muted-foreground">(opcional)</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {hasManual && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onPick(manual)}
              className="rounded-md border border-info/30 bg-info/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-info hover:bg-info/15"
            >
              pick ${manual.toFixed(0)}
            </button>
          </TooltipTrigger>
          <TooltipContent>Stake que cargaste al crear el pick</TooltipContent>
        </Tooltip>
      )}
      {hasKelly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onPick(kelly)}
              className="rounded-md border border-positive/30 bg-positive/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-positive hover:bg-positive/15"
            >
              kelly ${kelly.toFixed(0)}
            </button>
          </TooltipTrigger>
          <TooltipContent>Recomendación del Kelly Engine</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
